import { Menu, X } from "lucide-react";
import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Check if current page is home page
  const isHomePage = location.pathname === "/";

  return (
    <>
      <nav
        className={`fixed h-20 px-6 flex text-lg items-center top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled
            ? "bg-black/30 backdrop-blur-lg  rounded-b-full py-2 mx-auto w-full border-b-1 border-gray-700 shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1024px] w-full mx-auto px-4 md:px-6 lg:px-8">
          {/* Reduced height and added top margin when scrolled */}
          <div
            className={
              "flex items-center justify-between transition-all duration-300 "
            }
          >
            {/* Logo */} 
            <div className="flex-shrink-0">
              <NavLink
                to="/"
                className="font-bold text-2xl md:text-3xl tracking-tight hover:opacity-80 transition-opacity"
                onClick={closeMobileMenu}
              >
                <span className="text-primary">Hegra</span>
                <span
                  className={`relative inline-block ${
                    isHomePage
                      ? "text-white"
                      : isScrolled
                      ? "text-white"
                      : "text-gray-900"
                  }`}
                >
                  Vision
                  {/* Animated red underline */}
                  <span
                    className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-primary via-primary-light to-primary animate-underline rounded-full"
                    style={{
                      boxShadow: "0 0 6px rgba(220,38,38,0.3)",
                    }}
                  />
                </span>
              </NavLink>
            </div>

            {/* Desktop Menu - curved container only when scrolled */}
            <div className={"hidden md:block transition-all duration-300 "}>
              <ul className="flex items-center gap-2">
                <li>
                  <NavLink
                    to="/"
                    className={({ isActive }) =>
                      `px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                        isActive
                          ? "bg-primary text-white hover:text-white hover:bg-red-800"
                          : isHomePage
                          ? isScrolled
                            ? "text-gray-100 hover:text-white hover:bg-white/10"
                            : "text-white hover:text-gray-900 hover:bg-gray-100"
                          : isScrolled
                          ? "text-white hover:text-gray-900 hover:bg-gray-100"
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      }`
                    }
                  >
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/courses"
                    className={({ isActive }) =>
                      `px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                        isActive
                          ? "bg-primary text-white hover:text-white hover:bg-red-800"
                          : isHomePage
                          ? isScrolled
                            ? "text-gray-100 hover:text-white hover:bg-white/10"
                            : "text-white hover:text-gray-900 hover:bg-gray-100"
                          : isScrolled
                          ? "text-white hover:text-gray-900 hover:bg-gray-100"
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      }`
                    }
                  >
                    Courses
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/about"
                    className={({ isActive }) =>
                      `px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                        isActive
                          ? "bg-primary text-white hover:text-white hover:bg-red-800"
                          : // : 'text-white hover:text-black hover:bg-gray-100'
                          isHomePage
                          ? isScrolled
                            ? "text-gray-100 hover:text-white hover:bg-white/10"
                            : "text-white hover:text-gray-900 hover:bg-gray-100"
                          : isScrolled
                          ? "text-white hover:text-gray-900 hover:bg-gray-100"
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      }`
                    }
                  >
                    About Us
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/contact"
                    className={({ isActive }) =>
                      `px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                        isActive
                          ? "bg-primary text-white hover:text-white hover:bg-red-800"
                          : isHomePage
                          ? isScrolled
                            ? "text-gray-100 hover:text-white hover:bg-white/10"
                            : "text-white hover:text-gray-900 hover:bg-gray-100"
                          : isScrolled
                          ? "text-white hover:text-gray-900 hover:bg-gray-100"
                          : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                      }`
                    }
                  >
                    Contact Us
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Mobile Menu Button */}
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-all duration-1000 ${
                isHomePage
                  ? "text-white hover:bg-red-600 bg-red-500"
                  : "text-white bg-red-500 hover:bg-red-600"
              }`}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-6">
                <Menu
                  className={`absolute inset-0 w-6 h-6 transition-all duration-500 ${
                    isMobileMenuOpen
                      ? "rotate-90 opacity-0 scale-0"
                      : "rotate-0 opacity-100 scale-100"
                  }`}
                />
                <X
                  className={`absolute inset-0 w-6 h-6 transition-all duration-500 ${
                    isMobileMenuOpen
                      ? "rotate-0 opacity-100 scale-100"
                      : "-rotate-90 opacity-0 scale-0"
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`fixed top-20 left-0 right-0 z-40 md:hidden transition-all duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <div className="mx-4 bg-black/10 backdrop-blur-xl rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
          <ul className="flex flex-col py-4 px-2">
            <li>
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `block mx-2 my-1 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-primary text-white hover:text-white hover:bg-red-800"
                      : "text-white hover:text-gray-900 hover:bg-gray-100"
                  }`
                }
                onClick={closeMobileMenu}
              >
                Home
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/courses"
                className={({ isActive }) =>
                  `block mx-2 my-1 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-primary text-white hover:text-white hover:bg-red-800"
                      : "text-white hover:text-gray-900 hover:bg-gray-100"
                  }`
                }
                onClick={closeMobileMenu}
              >
                Courses
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `block mx-2 my-1 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-red-500 text-white hover:text-white"
                      : "text-white hover:text-black hover:bg-gray-100"
                  }`
                }
                onClick={closeMobileMenu}
              >
                About Us
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contact"
                className={({ isActive }) =>
                  `block mx-2 my-1 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-primary text-white hover:text-white hover:bg-red-800"
                      : "text-white hover:text-gray-900 hover:bg-gray-100"
                  }`
                }
                onClick={closeMobileMenu}
              >
                Contact Us
              </NavLink>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Navbar;
